import { create } from 'zustand';

export const ToastKind = { Info: 'info', Error: 'error', Notice: 'notice' } as const;
export type ToastKind = (typeof ToastKind)[keyof typeof ToastKind];

/** Time a toast with an undo button stays on screen (requirements §3.9). */
export const UNDO_TOAST_MS = 10_000;
export const INFO_TOAST_MS = 3_000;
export const ERROR_TOAST_MS = 6_000;
const MAX_TOASTS = 4;

export interface ToastAction {
  readonly label: string;
  readonly onAction: () => void;
}

export interface Toast {
  readonly id: number;
  readonly kind: ToastKind;
  readonly message: string;
  readonly action?: ToastAction;
  /** The undo-history version this toast's action belongs to; the action is hidden once it moves on. */
  readonly undoVersion?: number;
  /** Runs when the toast is dismissed (by the user or by its timer), not when `clear` removes it. */
  readonly onDismiss?: () => void;
}

interface ToastState {
  readonly toasts: readonly Toast[];
  push(toast: Omit<Toast, 'id'>, durationMs: number): number;
  dismiss(id: number): void;
  clear(): void;
}

let nextId = 1;
const timers = new Map<number, number>();

function cancelTimer(id: number): void {
  const timer = timers.get(id);
  if (timer !== undefined) window.clearTimeout(timer);
  timers.delete(id);
}

export const useToastStore = create<ToastState>()((set, get) => ({
  toasts: [],
  push(toast, durationMs) {
    const id = nextId++;
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }].slice(-MAX_TOASTS) }));
    if (durationMs > 0)
      timers.set(
        id,
        window.setTimeout(() => get().dismiss(id), durationMs),
      );
    return id;
  },
  dismiss(id) {
    cancelTimer(id);
    const dismissed = get().toasts.find((toast) => toast.id === id);
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
    dismissed?.onDismiss?.();
  },
  clear() {
    timers.forEach((_, id) => cancelTimer(id));
    set({ toasts: [] });
  },
}));

const push = (toast: Omit<Toast, 'id'>, durationMs: number): number =>
  useToastStore.getState().push(toast, durationMs);

export const toastInfo = (message: string, durationMs: number = INFO_TOAST_MS): number =>
  push({ kind: ToastKind.Info, message }, durationMs);
export const toastError = (message: string): number =>
  push({ kind: ToastKind.Error, message }, ERROR_TOAST_MS);
/** A notice that stays until dismissed; `onDismiss` runs then (for remembering that it was seen). */
export const toastNotice = (message: string, onDismiss?: () => void): number =>
  push({ kind: ToastKind.Notice, message, ...(onDismiss === undefined ? {} : { onDismiss }) }, 0);

/** A toast with an undo button, valid for 10 seconds or until the undo history changes. */
export function toastUndo(message: string, label: string, undoVersion: number, onAction: () => void): number {
  return push({ kind: ToastKind.Info, message, action: { label, onAction }, undoVersion }, UNDO_TOAST_MS);
}
